import type { EChartsOption } from 'echarts'
import {
  axisTextStyle,
  deltaColor,
  formatCompactNumber,
  formatShare,
  formatSignedShare,
  isFiniteNumber,
  relativeDelta,
  richSafe,
  tooltipBase,
  tooltipHtml,
} from './format'
import { seriesColor, type ChartTheme } from './palette'

export const OTHER_KEY = '__other__'

export interface RankedBarItem {
  key: string
  label: string
  value: number | null
  /** Значение в периоде сравнения — для Δ. */
  previous?: number | null
  /** Доля в итоге 0..1; если не задана — считается от суммы показанных и «Прочего». */
  share?: number | null
  /** Подпись мельче под названием (в подсказке). */
  sublabel?: string | null
  /** Можно ли провалиться (по умолчанию — да; у «Прочего» — нет). */
  drillable?: boolean
  /**
   * Служебная строка («Не сопоставлено»): серым цветом «Прочего», всегда последней — после топа и «Прочего»,
   * в топ-N не входит и в «Прочее» не сворачивается.
   */
  muted?: boolean
}

export interface RankedBarsInput {
  items: RankedBarItem[]
  /** Сколько показать отдельно; остальное сворачивается в «Прочее». */
  topN?: number
  otherLabel?: string
  /**
   * Итог по всем объектам (если известен с сервера): «Прочее» = итог − показанные. Без него «Прочее» —
   * сумма свёрнутых строк `items`.
   */
  total?: number | null
  previousTotal?: number | null
  valueFormat?: (value: number) => string
  /** Показывать Δ к сравнению в подписи. */
  showDelta?: boolean
  /**
   * Показывать долю в итоге (по умолчанию — да). Для нескладываемых метрик (доля с продажами, дни до первой
   * продажи) доля бессмысленна — её выключают.
   */
  showShare?: boolean
  /** Рост — плохо. */
  invertDelta?: boolean
  /** Цвет столбцов: по умолчанию слот 1 — одна серия, одинаковые столбцы (цвет не дублирует длину). */
  color?: string
  /**
   * Порядок строк: `desc` (по умолчанию) — больше сверху; `asc` — меньше сверху (дефицит: «меньше всего дней
   * покрытия»); `input` — как пришло. Пустые значения всегда в конце.
   */
  order?: RankedBarsOrder
  /**
   * Сворачивать хвост в «Прочее» (по умолчанию — да). Для нескладываемых метрик (дни покрытия, скорость)
   * сумма хвоста бессмысленна — тогда хвост просто не показывается.
   */
  showOther?: boolean
  /**
   * Пороги — вертикальные отметки на оси значений с подписью («мало — 15 дн.», «излишек — 60 дн.»). Ось
   * расширяется, чтобы отметка была видна и при малых значениях.
   */
  markers?: RankedBarMarker[]
}

export type RankedBarsOrder = 'desc' | 'asc' | 'input'

export interface RankedBarMarker {
  value: number
  label: string
}

/**
 * «Топ-N + Прочее». Строки сортируются по убыванию значения (или по `order`); «Прочее» всегда последним. Пустые
 * значения (`null`) идут после чисел и в «Прочее» не складываются как ноль.
 */
export function foldTopN(input: RankedBarsInput): RankedBarItem[] {
  const topN = Math.max(1, input.topN ?? 10)
  const order = input.order ?? 'desc'
  const regular = input.items.filter((item) => !item.muted)
  const muted = input.items.filter((item) => item.muted)
  const sorted =
    order === 'input'
      ? [...regular]
      : [...regular].sort((a, b) => {
          const aEmpty = !isFiniteNumber(a.value)
          const bEmpty = !isFiniteNumber(b.value)
          if (aEmpty || bEmpty) return Number(aEmpty) - Number(bEmpty)
          return order === 'asc' ? (a.value as number) - (b.value as number) : (b.value as number) - (a.value as number)
        })
  const head = sorted.slice(0, topN)
  const tail = input.showOther === false ? [] : sorted.slice(topN)

  const sum = (values: Array<number | null | undefined>) => {
    const numbers = values.filter(isFiniteNumber)
    return numbers.length > 0 ? numbers.reduce((acc, value) => acc + value, 0) : null
  }

  let otherValue: number | null = null
  let otherPrevious: number | null = null
  if (input.showOther === false) {
    // Хвост не сворачиваем: «Прочего» нет.
  } else if (isFiniteNumber(input.total)) {
    const shown = sum([...head, ...muted].map((item) => item.value)) ?? 0
    const rest = input.total - shown
    otherValue = rest > 0.5 ? rest : null
    if (isFiniteNumber(input.previousTotal)) {
      const shownPrevious = sum([...head, ...muted].map((item) => item.previous)) ?? 0
      otherPrevious = input.previousTotal - shownPrevious
    }
  } else if (tail.length > 0) {
    otherValue = sum(tail.map((item) => item.value))
    otherPrevious = sum(tail.map((item) => item.previous))
  }

  const result = [...head]
  if (otherValue !== null && otherValue !== 0) {
    result.push({
      key: OTHER_KEY,
      label: input.otherLabel ?? 'Прочее',
      value: otherValue,
      previous: otherPrevious,
      drillable: false,
    })
  }
  result.push(...muted)

  const grand = isFiniteNumber(input.total) ? input.total : sum(result.map((item) => item.value))
  return result.map((item) => ({
    ...item,
    share: isFiniteNumber(item.share) ? item.share : isFiniteNumber(item.value) && grand ? item.value / grand : null,
  }))
}

/** Высота под число строк: 32 px на строку + поля. */
export function rankedBarsHeight(rows: number): number {
  return Math.max(120, rows * 32 + 16)
}

/**
 * Горизонтальные столбцы рейтинга: один цвет на все (это одна серия), «Прочее» — серым; значение, доля и Δ —
 * подписью справа цветом текста; толщина ≤ 24 px, скругление 4 px на конце, общий базовый ноль.
 */
export function buildRankedBarsOption(input: RankedBarsInput, theme: ChartTheme): EChartsOption {
  const rows = foldTopN(input)
  const valueFormat = input.valueFormat ?? ((value: number) => formatCompactNumber(value))
  const main = input.color ?? seriesColor(theme, 0)
  const showDelta = input.showDelta ?? true
  const markers = (input.markers ?? []).filter((marker) => isFiniteNumber(marker.value))
  const markerMax = markers.reduce((max, marker) => Math.max(max, marker.value), 0)

  const labelFor = (item: RankedBarItem) => {
    const parts: string[] = []
    parts.push(`{value|${isFiniteNumber(item.value) ? valueFormat(item.value) : '—'}}`)
    if (input.showShare !== false && isFiniteNumber(item.share)) parts.push(`{muted|${formatShare(item.share)}}`)
    if (showDelta) {
      const delta = relativeDelta(item.value, item.previous)
      if (delta !== null) {
        const color = deltaColor(theme, delta, input.invertDelta)
        parts.push(color === theme.positiveText ? `{up|${formatSignedShare(delta)}}` : color === theme.negativeText ? `{down|${formatSignedShare(delta)}}` : `{muted|${formatSignedShare(delta)}}`)
      }
    }
    return parts.join('  ')
  }

  return {
    backgroundColor: 'transparent',
    animationDuration: 300,
    textStyle: { fontFamily: theme.fontFamily },
    aria: { enabled: true },
    grid: { top: markers.length > 0 ? 22 : 4, left: 8, right: 8, bottom: 4, outerBoundsMode: 'same', outerBoundsContain: 'axisLabel' } as any,
    tooltip: {
      ...tooltipBase(theme),
      trigger: 'item',
      formatter: (params: any) => {
        const item = rows[params.dataIndex]
        if (!item) return ''
        const delta = relativeDelta(item.value, item.previous)
        return tooltipHtml(
          theme,
          item.sublabel ? `${item.label} · ${item.sublabel}` : item.label,
          [
            {
              color: item.key === OTHER_KEY || item.muted ? theme.other : main,
              label: 'текущий период',
              value: isFiniteNumber(item.value) ? valueFormat(item.value) : '—',
              note: input.showShare !== false && isFiniteNumber(item.share) ? `доля ${formatShare(item.share)}` : undefined,
            },
            ...(item.previous !== undefined
              ? [
                  {
                    label: 'сравнение',
                    value: isFiniteNumber(item.previous) ? valueFormat(item.previous) : '—',
                    note: delta !== null ? formatSignedShare(delta) : undefined,
                    noteColor: deltaColor(theme, delta, input.invertDelta),
                  },
                ]
              : []),
          ],
        )
      },
    },
    xAxis: {
      type: 'value',
      show: false,
      max: (extent: { max: number }) => Math.max(extent.max, markerMax * 1.05) * 1.45,
    },
    yAxis: {
      type: 'category',
      inverse: true,
      data: rows.map((item) => item.label),
      axisLine: { show: true, lineStyle: { color: theme.axis } },
      axisTick: { show: false },
      axisLabel: { ...axisTextStyle(theme), color: theme.textSecondary, fontSize: 12, width: 180, overflow: 'truncate' },
    },
    series: [
      {
        type: 'bar',
        barMaxWidth: 24,
        barCategoryGap: '30%',
        cursor: 'pointer',
        data: rows.map((item) => ({
          value: isFiniteNumber(item.value) ? item.value : '-',
          itemStyle: {
            color: item.key === OTHER_KEY || item.muted ? theme.other : main,
            borderRadius: [0, 4, 4, 0],
          },
          cursor: item.key === OTHER_KEY || item.drillable === false ? 'default' : 'pointer',
        })),
        label: {
          show: true,
          position: 'right',
          distance: 8,
          formatter: (params: any) => labelFor(rows[params.dataIndex]),
          rich: {
            value: { color: theme.text, fontSize: 12, fontWeight: 600, fontFamily: theme.fontFamily },
            muted: { color: theme.textMuted, fontSize: 11, fontFamily: theme.fontFamily },
            up: { color: theme.positiveText, fontSize: 11, fontFamily: theme.fontFamily },
            down: { color: theme.negativeText, fontSize: 11, fontFamily: theme.fontFamily },
          },
        },
        emphasis: { itemStyle: { opacity: 0.85 } },
        ...(markers.length > 0
          ? {
              markLine: {
                silent: true,
                symbol: ['none', 'none'],
                animation: false,
                lineStyle: { color: theme.textMuted, width: 1, type: 'dashed', opacity: 0.8 },
                label: {
                  show: true,
                  position: 'end',
                  color: theme.textSecondary,
                  fontSize: 11,
                  fontFamily: theme.fontFamily,
                  formatter: (params: any) => richSafe(params?.name ?? ''),
                },
                data: markers.map((marker) => ({ name: marker.label, xAxis: marker.value })),
              },
            }
          : {}),
      },
    ],
  } as EChartsOption
}
